/**
 * Agency Portal v2 §4.1 — typed client for the agency overview
 * endpoint. One-shot read that powers the rail counts, the stat tiles
 * on the Overview page, Next steps, and Recent activity.
 */

import { ApiError, type HttpClient } from './http-client.js';

export type AgencyWidgetState = 'not_set_up' | 'needs_origin' | 'live';

export interface AgencyOverviewActivity {
  readonly at: string;
  readonly kind: string;
  readonly domain: string | null;
}

/**
 * ADR-0263 §7.5 as amended for security (2026-11-03) — Agency Portal
 * header counter for the "Client sites X of Y" row. `null` when the
 * agency's v2 plan does not set `included_sites`; the header hides
 * the row.
 */
export interface AgencyClientSitesCounter {
  readonly used: number;
  readonly cap: number;
}

/**
 * ADR-0263 §7.5 as amended for security (2026-11-03) — Agency Portal
 * header counter for the "Scans X of Y this month" row. Counts
 * `bulk_scan` only (widget stays isolated per widget-isolation review).
 * `null` when the agency's v2 plan does not set `agency_scans_monthly`.
 */
export interface AgencyScansCounter {
  readonly used: number;
  readonly cap: number;
  readonly reset_at_iso: string;
}

export interface AgencyOverviewWire {
  readonly clients_linked: number;
  readonly reports_scanned_30d: number;
  readonly reports_sent_30d: number;
  readonly referral: {
    readonly slug: string | null;
    readonly clicks_7d: number;
    readonly clicks_30d: number;
    readonly signups_total: number;
  };
  readonly widget: {
    readonly state: AgencyWidgetState;
    readonly leads_30d: number;
  };
  readonly white_label: {
    readonly configured: boolean;
  };
  readonly client_sites: AgencyClientSitesCounter | null;
  readonly scans: AgencyScansCounter | null;
  readonly next_steps: {
    readonly first_client: boolean;
    readonly first_scan: boolean;
    readonly widget_setup: boolean;
    readonly white_label_logo: boolean;
    readonly referral_slug_set: boolean;
  };
  readonly activity: readonly AgencyOverviewActivity[];
}

export type GetAgencyOverviewResult =
  | { readonly kind: 'ok'; readonly state: AgencyOverviewWire }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export async function getAgencyOverview(
  http: HttpClient,
): Promise<GetAgencyOverviewResult> {
  try {
    const res = await http.request<AgencyOverviewWire>({
      method: 'GET',
      path: '/v1/agency/overview',
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', state: res };
  } catch (err) {
    if (err instanceof ApiError && err.kind === 'forbidden') {
      return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
    }
    return { kind: 'error', message: err instanceof Error ? err.message : 'unknown_error' };
  }
}

/** v4 (2026-10-10) — paginated + grouped activity feed. */
export interface AgencyActivityEntry {
  readonly at: string;
  readonly kind: string;
  readonly domain: string | null;
  readonly summary: string;
  readonly href?: string;
}

export interface AgencyActivityPage {
  readonly activity: readonly AgencyActivityEntry[];
  readonly next_cursor: string | null;
  readonly page_size: number;
}

export type GetAgencyActivityResult =
  | { readonly kind: 'ok'; readonly page: AgencyActivityPage }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export async function getAgencyActivity(
  http: HttpClient,
  opts: { limit?: number; cursor?: string | null } = {},
): Promise<GetAgencyActivityResult> {
  const q = new URLSearchParams();
  if (opts.limit !== undefined) q.set('limit', String(opts.limit));
  if (opts.cursor !== null && opts.cursor !== undefined) q.set('cursor', opts.cursor);
  const path = `/v1/agency/activity${q.toString().length > 0 ? `?${q}` : ''}`;
  try {
    const res = await http.request<AgencyActivityPage>({ method: 'GET', path });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', page: res };
  } catch (err) {
    if (err instanceof ApiError && err.kind === 'forbidden') {
      return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
    }
    return { kind: 'error', message: err instanceof Error ? err.message : 'unknown_error' };
  }
}
